import { Award, Shield, Leaf, CheckCircle } from "lucide-react";

const certifications = [
  {
    name: "ISO 9001:2015",
    description: "Quality Management System certification ensuring consistent quality.",
    icon: Award,
    category: "Quality",
  },
  {
    name: "GOTS Certified",
    description: "Global Organic Textile Standard for organic cotton products.",
    icon: Leaf,
    category: "Organic",
  },
  {
    name: "OEKO-TEX Standard 100",
    description: "Tested for harmful substances, safe for human use.",
    icon: Shield,
    category: "Safety",
  },
  {
    name: "Fair Trade Certified",
    description: "Ensuring fair wages and ethical working conditions.",
    icon: CheckCircle,
    category: "Ethical",
  },
  {
    name: "SEDEX Member",
    description: "Supplier Ethical Data Exchange membership for ethical trade.",
    icon: Shield,
    category: "Ethical",
  },
  {
    name: "FSC Certified",
    description: "Forest Stewardship Council certification for sustainable sourcing.",
    icon: Leaf,
    category: "Sustainability",
  },
];

const compliance = [
  "European Union REACH Compliance",
  "California Prop 65 Compliant",
  "CPSIA Compliant for US Markets",
  "UK Modern Slavery Act Compliance",
  "AZO Free Dyes Certification",
  "Phthalate Free Materials",
];

export const CertificationsPage = () => {
  return (
    <div className="bg-cream">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-cream-dark">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-forest mb-6">
              Certifications & <span className="text-sand">Compliance</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We maintain the highest standards of quality, safety, and ethical
              practices. Our certifications ensure that every product meets
              international standards and regulations.
            </p>
          </div>
        </div>
      </section>

      {/* Certifications Grid */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest text-center mb-12">
            Our Certifications
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {certifications.map((cert, index) => (
              <div
                key={index}
                data-testid={`cert-${index}`}
                className="bg-white rounded-xl p-6 border border-sand/20 shadow-card hover-lift"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-forest/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <cert.icon className="w-7 h-7 text-forest" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-sand uppercase tracking-wider">
                      {cert.category}
                    </span>
                    <h3 className="font-heading text-xl font-semibold text-forest mt-1 mb-2">
                      {cert.name}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {cert.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-16 md:py-24 bg-cream-dark">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest mb-8">
                Regulatory Compliance
              </h2>
              <p className="text-muted-foreground mb-6">
                Our products comply with all major international regulations and
                standards, ensuring safe and legal import into any country.
              </p>
              <ul className="space-y-3">
                {compliance.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-forest flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-forest rounded-2xl p-8 text-white">
              <h3 className="font-heading text-2xl font-semibold mb-4">
                Quality Assurance
              </h3>
              <p className="text-white/80 mb-6">
                Every batch of products undergoes rigorous testing and inspection
                to ensure compliance with all applicable standards.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold">100%</p>
                  <p className="text-sm text-white/70">Inspection Rate</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold">&lt;0.5%</p>
                  <p className="text-sm text-white/70">Defect Rate</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testing */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest mb-8">
            Third-Party Testing
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-12">
            We partner with internationally recognized testing laboratories to
            verify the safety and quality of our products.
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            {["SGS", "Bureau Veritas", "Intertek", "TÜV"].map((lab, index) => (
              <div
                key={index}
                className="bg-white rounded-xl px-8 py-6 border border-sand/20 shadow-card"
              >
                <span className="font-heading text-xl font-bold text-forest">
                  {lab}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
